// src/pages/TrialStatus.js
// Parent-facing trial status screen at /trial-status.
//
// Standalone authed page (its own onAuthStateChanged, like Dashboard) — NOT
// wrapped in ProtectedRoute, so the ProtectedRoute trial guard can redirect
// here without a loop. Viewable by ANY signed-in user with an open trial: a
// trial-only family AND a paying family that booked a trial for another child.
// A signed-in user with no trials gets a clear empty state, not an error.
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Crown, Sparkles, Calendar, Video, LogOut, Home, AlertTriangle } from 'lucide-react';
import { TRIAL_STATUS_LABELS, getTrialTimezone } from '../utils/trialHelper';
import { formatISOForDisplay, formatFriendlyDate } from '../utils/timezoneUtils';
import {
  readPendingTrialSignup,
  recordPendingTrialSignupAttempt,
  clearPendingTrialSignup,
  MAX_RESUME_ATTEMPTS,
} from '../utils/pendingTrialSignup';
import Footer from '../components/Footer';

// Parent-friendly copy per trial status. Falls back to TRIAL_STATUS_LABELS for
// any status not listed (keeps working if the status machine grows).
const PARENT_STATUS = {
  requested: {
    label: 'Request received',
    hint: "We've got your request and will be in touch soon to schedule your class.",
    cls: 'bg-blue-100 text-blue-800',
  },
  scheduled: {
    label: 'Trial booked',
    hint: 'Your free trial class is scheduled — details below.',
    cls: 'bg-green-100 text-green-800',
  },
  held: {
    label: 'Trial completed',
    hint: 'Your trial is complete — we hope you enjoyed it!',
    cls: 'bg-purple-100 text-purple-800',
  },
  no_show: {
    label: 'Trial missed',
    hint: 'It looks like this trial was missed. Get in touch and we will rebook it.',
    cls: 'bg-orange-100 text-orange-800',
  },
  cancelled: {
    label: 'Cancelled',
    hint: 'This trial was cancelled.',
    cls: 'bg-gray-100 text-gray-700',
  },
  won: {
    label: 'Trial complete',
    hint: 'Your trial is complete — we hope you enjoyed it!',
    cls: 'bg-purple-100 text-purple-800',
  },
  lost: {
    label: 'Closed',
    hint: 'This request is closed. Reach out any time to try again.',
    cls: 'bg-gray-100 text-gray-700',
  },
  expired: {
    label: 'Expired',
    hint: 'This request expired. Contact us to book a new trial.',
    cls: 'bg-gray-100 text-gray-700',
  },
};

// PHASE 2 — how loudly /trial-status prompts the parent to enrol. Payment is
// never gated on trial status (see /enroll); this only picks the prominence:
//   'primary'    — the parent most ready to buy: a full CTA card + button
//   'secondary'  — a quiet "Already decided? Enrol now." link
//   'reconsider' — same quiet link, softer copy, for a closed trial
//   null         — no prompt (already enrolled)
function enrolPromptTone(trial, converted) {
  if (converted) return null;
  if (['held', 'won', 'no_show', 'cancelled'].includes(trial.status)) return 'primary';
  if (trial.status === 'requested' || trial.status === 'scheduled') return 'secondary';
  if (trial.status === 'lost' || trial.status === 'expired') return 'reconsider';
  return null;
}

function TrialCard({ trial }) {
  const navigate = useNavigate();

  // Keyed on convertedPackageId, NOT convertedAt: the trial_conversion webhook
  // sets convertedPackageId only on a completed PAYMENT (and the child gains a
  // course). A `won` status — even with convertedAt — can be hand-set by an
  // admin in TrialManager with no payment, leaving the child course:null;
  // calling that "enrolled" would be the Phase 1 bug, relocated.
  const converted = !!trial.convertedPackageId;

  const meta = converted
    ? {
      label: 'Enrolled',
      hint: "You're enrolled — welcome aboard! We'll be in touch with your class details.",
      cls: 'bg-green-100 text-green-800',
    }
    : (PARENT_STATUS[trial.status] || {
      label: TRIAL_STATUS_LABELS[trial.status] || trial.status,
      hint: '',
      cls: 'bg-gray-100 text-gray-700',
    });

  const tz = getTrialTimezone(trial);
  const futureSlot = trial.scheduledAt && new Date(trial.scheduledAt) > new Date();
  const showSchedule = trial.scheduledAt && (trial.status === 'scheduled' || (converted && futureSlot));
  const tone = enrolPromptTone(trial, converted);

  return (
    <div className="bg-white rounded-2xl p-6 shadow-xl border border-purple-100">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-lg font-bold text-gray-800">{trial.studentName}</div>
          <div className="text-sm text-gray-500">
            {trial.studentType === 'adult' ? 'Adult learner' : 'Child'}
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-bold ${meta.cls}`}>{meta.label}</span>
      </div>

      {meta.hint && <p className="text-sm text-gray-600 mt-3">{meta.hint}</p>}

      {tone === 'primary' && (
        <div className="mt-4 rounded-xl bg-purple-50 border border-purple-100 p-4">
          <p className="text-sm font-semibold text-gray-800">Ready to enrol {trial.studentName}?</p>
          <button
            onClick={() => navigate('/enroll')}
            className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-xl transition-colors"
          >
            <Crown size={15} /> Enrol {trial.studentName}
          </button>
        </div>
      )}

      {showSchedule && (
        <div className="mt-4 space-y-2 border-t border-gray-100 pt-4 text-sm">
          <div className="flex items-center gap-2 text-gray-700">
            <Calendar size={16} className="text-purple-500 flex-shrink-0" />
            <span>
              {formatFriendlyDate(trial.scheduledAt, tz || 'UTC')}
              {' · '}
              {formatISOForDisplay(trial.scheduledAt, tz || 'UTC')}
              {tz ? ` (${tz})` : ' (UTC)'}
            </span>
          </div>
          {trial.teacherName && (
            <div className="text-gray-700 pl-6">Teacher: {trial.teacherName}</div>
          )}
          {trial.meetingUrl && (
            <a
              href={trial.meetingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              <Video size={16} /> Join class
            </a>
          )}
        </div>
      )}

      {(tone === 'secondary' || tone === 'reconsider') && (
        <p className="mt-4 text-sm text-gray-500">
          {tone === 'reconsider' ? 'Changed your mind? You can ' : 'Already decided? '}
          <button
            onClick={() => navigate('/enroll')}
            className="text-purple-600 hover:text-purple-800 underline font-medium"
          >
            {tone === 'reconsider' ? 'enrol any time' : 'Enrol now'}
          </button>
        </p>
      )}
    </div>
  );
}

function TrialStatus() {
  const navigate = useNavigate();
  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState(null);
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Pending trial signup that couldn't be finished during login (existing-email
  // path). { payload, attempts } or null. The "try again" card below re-calls
  // createTrialSignup with the stashed payload.
  const [pending, setPending] = useState(null);
  const [resuming, setResuming] = useState(false);
  const [resumeError, setResumeError] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u) {
        navigate('/login', { replace: true });
        return;
      }
      setUser(u);
      setAuthReady(true);
    });
    return () => unsub();
  }, [navigate]);

  const loadTrials = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      setError(false);
      const snap = await getDocs(query(collection(db, 'trials'), where('userId', '==', user.uid)));
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
      setTrials(rows);
    } catch (err) {
      console.error('TrialStatus: failed to load trials', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadTrials(); }, [loadTrials]);

  useEffect(() => {
    if (!user) return;
    setPending(readPendingTrialSignup(user.email));
  }, [user]);

  const handleResumeRetry = async () => {
    if (!pending || pending.attempts >= MAX_RESUME_ATTEMPTS) return;
    setResuming(true);
    setResumeError(false);
    const attempts = recordPendingTrialSignupAttempt();
    try {
      await httpsCallable(getFunctions(), 'createTrialSignup')(pending.payload);
      clearPendingTrialSignup();
      setPending(null);
      await loadTrials();
    } catch (err) {
      console.error('TrialStatus: retry of pending trial signup failed', err);
      setPending({ ...pending, attempts });
      setResumeError(true);
    } finally {
      setResuming(false);
    }
  };

  const handleSignOut = async () => {
    try { await signOut(auth); } catch { /* ignore */ }
    navigate('/login', { replace: true });
  };

  if (!authReady || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <div className="text-purple-600 text-xl font-bold">Loading your trial…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      <div className="container mx-auto px-4 py-10 max-w-2xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-5 py-2 mb-4">
            <Sparkles className="text-yellow-400" size={20} />
            <span className="text-white font-bold">My Roots</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-2">Your Free Trial</h1>
        </div>

        {/* Resume card — a trial request that couldn't be finished at login. */}
        {pending && (
          <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-orange-200 mb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-orange-500 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <div className="font-bold text-gray-800">We couldn't finish your trial request</div>
                {pending.attempts >= MAX_RESUME_ATTEMPTS ? (
                  <p className="text-sm text-gray-600 mt-1">
                    Please contact us at{' '}
                    <a href="mailto:info@my-roots.ca" className="text-purple-600 underline font-medium">info@my-roots.ca</a>
                    {' '}and we'll book it for you.
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 mt-1">
                      {resumeError
                        ? "That didn't go through. Please try again."
                        : 'Your account is fine — we just need to submit the request.'}
                    </p>
                    <button
                      onClick={handleResumeRetry}
                      disabled={resuming}
                      className="mt-3 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50"
                    >
                      {resuming ? 'Submitting…' : 'Try again'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {error ? (
          <div className="bg-white rounded-2xl p-8 shadow-xl text-center">
            <div className="text-4xl mb-3">😕</div>
            <div className="text-lg font-bold text-gray-800 mb-2">We couldn't load your trial</div>
            <p className="text-gray-600 mb-6">Please refresh the page, or try again in a moment.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl transition-colors"
            >
              Refresh
            </button>
          </div>
        ) : trials.length > 0 ? (
          <div className="space-y-4">
            {trials.map((t) => (
              <TrialCard key={t.id} trial={t} />
            ))}
            <p className="text-center text-sm text-purple-200 pt-2">
              Questions? Email us at{' '}
              <a href="mailto:info@my-roots.ca" className="underline font-medium">info@my-roots.ca</a>
            </p>
          </div>
        ) : !pending ? (
          <div className="bg-white rounded-2xl p-8 shadow-xl text-center">
            <Crown className="text-purple-400 mx-auto mb-3" size={36} />
            <div className="text-lg font-bold text-gray-800 mb-2">No trial requests yet</div>
            <p className="text-gray-600 mb-6">
              There's nothing to show here right now. If you were expecting a trial, email us at{' '}
              <a href="mailto:info@my-roots.ca" className="text-purple-600 underline font-medium">info@my-roots.ca</a>
              {' '}and we'll sort it out.
            </p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl transition-colors"
            >
              <Home size={16} /> Back to Home
            </button>
          </div>
        ) : null}

        <div className="flex items-center justify-center gap-4 mt-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-purple-200 hover:text-white text-sm font-medium transition-colors"
          >
            <Home size={15} /> Home
          </button>
          <span className="text-purple-400">·</span>
          <button
            onClick={handleSignOut}
            className="inline-flex items-center gap-2 text-purple-200 hover:text-white text-sm font-medium transition-colors"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </div>
      <Footer variant="minimal" />
    </div>
  );
}

export default TrialStatus;
